import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Subscription } from 'rxjs';

import { environment } from '../environments/environment';
import { CustomerModule } from '../models/customer';
import { RegisterOAuth } from '../models/user';
import { AuthService } from '../shared-components/ng-login/auth/auth.service';
import { LoadingStore } from '../stores/loading.store';
import { ApiService } from './api.service';
import { languagesCognitoCodeMapping } from './cognito.service';
import { CustomerService } from './customer.service';
import { DomainConfigService } from './domain-config.service';

@Injectable({
  providedIn: 'root'
})
export class SocialLoginService {
  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private domainConfService = inject(DomainConfigService);
  private loading = inject(LoadingStore);
  private customerService = inject(CustomerService);

  private loginEuroinnovaOptions;

  loginResponse = new BehaviorSubject(false);
  private loginSuscription!: Subscription | undefined;

  constructor() {
    const customerConfig = this.domainConfService.getConfigForSubdomain(this.apiService.getDomain());
    const redirectUri = environment.production
      ? customerConfig.oauthRedirectPath || window.location.origin
      : window.location.origin;
    /* eslint-disable @typescript-eslint/naming-convention */
    this.loginEuroinnovaOptions = {
      baseUrl: customerConfig.socialLoginConfig?.euroinnova?.baseUrl,
      client_id: customerConfig.socialLoginConfig?.euroinnova?.clientId,
      response_type: 'code',
      scope: 'user_read',
      redirect_uri: redirectUri
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  async handleAuthState(authorizationCode?: string, language: string = ''): Promise<void> {
    if (authorizationCode) {
      const parameters = new RegisterOAuth(
        '',
        authorizationCode,
        this.loginEuroinnovaOptions.client_id,
        language,
        this.loginEuroinnovaOptions.redirect_uri
      );

      this.authService.login(parameters).subscribe((response) => {
        this.loading.stop();
        this.loginResponse.next(!!response);
      });
    }
  }

  async signin(data: string = ''): Promise<void> {
    const cognitoModule: CustomerModule = (await lastValueFrom(this.customerService.getCustomerModules())).modules
      .COGNITO_LOGIN;

    const baseUrl = cognitoModule.enabled
      ? `${cognitoModule.config.domain}/oauth2/authorize`
      : this.loginEuroinnovaOptions.baseUrl;

    /* eslint-disable @typescript-eslint/naming-convention */
    const params = new URLSearchParams(
      cognitoModule.enabled
        ? {
            response_type: 'code',
            scope: 'email openid profile',
            client_id: cognitoModule.config.client_id,
            redirect_uri: cognitoModule.config.redirect_url
          }
        : {
            client_id: this.loginEuroinnovaOptions.client_id,
            response_type: this.loginEuroinnovaOptions.response_type,
            redirect_uri: this.loginEuroinnovaOptions.redirect_uri,
            scope: this.loginEuroinnovaOptions.scope
          }
    );
    /* eslint-enable @typescript-eslint/naming-convention */

    data = languagesCognitoCodeMapping[data];

    if (data) {
      params.set('lang', data);
    }

    const url = `${baseUrl}?${params.toString()}`;

    window.location.replace(url);
  }

  unsubscribe(): void {
    if (this.loginSuscription) {
      this.loginSuscription.unsubscribe();
      this.loginSuscription = undefined;
    }
  }
}
